import { 
  Activity,
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  AlertCircle,
  AlertTriangle,
  UserPlus,
  Users,
  CheckCircle2,
  Package,
  PackageX,
  Truck,
  ShoppingCart,
  ClipboardList,
  Target,
  BarChart3,
  PieChart,
  Factory,
  Building2,
  Wallet,
  LucideIcon
} from "lucide-react";

export type WidgetCategory = 'financial' | 'sales' | 'logistics' | 'crm' | 'tasks' | 'inventory' | 'purchases' | 'production';

export type WidgetSize = 'full' | 'half' | 'third';

export interface WidgetConfig {
  type: string;
  name: string;
  description: string;
  icon: LucideIcon;
  category: WidgetCategory;
  defaultSize: WidgetSize;
  requiredPermission?: string;
}

export interface UserWidget {
  id: string;
  type: string;
  size: WidgetSize;
  position: number;
}

export const WIDGET_CATEGORIES: Record<WidgetCategory, { name: string; icon: LucideIcon }> = {
  financial: { name: 'Financeiro', icon: DollarSign },
  sales: { name: 'Vendas', icon: TrendingUp },
  logistics: { name: 'Logística', icon: Truck },
  crm: { name: 'CRM', icon: Users },
  tasks: { name: 'Tarefas', icon: CheckCircle2 },
  inventory: { name: 'Estoque', icon: Package },
  purchases: { name: 'Compras', icon: ShoppingCart },
  production: { name: 'Produção', icon: Factory },
};

export const AVAILABLE_WIDGETS: WidgetConfig[] = [
  // Financeiro - Novos widgets separados (padrão Conta Azul)
  {
    type: 'receivables_overview',
    name: 'A Receber',
    description: 'Visão geral: vencidos, vencem hoje e restante do mês',
    icon: TrendingUp,
    category: 'financial',
    defaultSize: 'half',
    requiredPermission: 'fin:view_reports',
  },
  {
    type: 'payables_overview',
    name: 'A Pagar',
    description: 'Visão geral: vencidos, vencem hoje e restante do mês',
    icon: TrendingDown,
    category: 'financial',
    defaultSize: 'half',
    requiredPermission: 'fin:view_reports',
  },
  {
    type: 'financial_accounts',
    name: 'Contas Financeiras',
    description: 'Saldos das contas e conciliações pendentes',
    icon: Building2,
    category: 'financial',
    defaultSize: 'half',
    requiredPermission: 'fin:view_reports',
  },
  // Financeiro - Widgets compactos legados
  {
    type: 'financial_summary',
    name: 'Resumo Financeiro (Compacto)',
    description: 'Visão geral compacta de contas a receber, a pagar e saldo',
    icon: DollarSign,
    category: 'financial',
    defaultSize: 'full',
    requiredPermission: 'fin:view_reports',
  },
  {
    type: 'receivables_today',
    name: 'A Receber Hoje',
    description: 'Total de contas a receber com vencimento hoje',
    icon: TrendingUp,
    category: 'financial',
    defaultSize: 'third',
    requiredPermission: 'fin:receive_pay',
  },
  {
    type: 'payables_today',
    name: 'A Pagar Hoje',
    description: 'Total de contas a pagar com vencimento hoje',
    icon: TrendingDown,
    category: 'financial',
    defaultSize: 'third',
    requiredPermission: 'fin:receive_pay',
  },
  {
    type: 'overdue_accounts',
    name: 'Contas Vencidas',
    description: 'Total de contas em atraso',
    icon: AlertCircle,
    category: 'financial',
    defaultSize: 'third',
    requiredPermission: 'fin:view_reports',
  },
  {
    type: 'sales_chart',
    name: 'Gráfico de Vendas',
    description: 'Faturamento mensal dos últimos 12 meses',
    icon: BarChart3,
    category: 'financial',
    defaultSize: 'half',
    requiredPermission: 'sales:view_dashboard',
  },
  {
    type: 'cash_flow_chart',
    name: 'Fluxo de Caixa Diário',
    description: 'Recebimentos, pagamentos e saldo acumulado com projeção futura',
    icon: TrendingUp,
    category: 'financial',
    defaultSize: 'half',
    requiredPermission: 'finance:view_cashflow',
  },
  {
    type: 'dre_summary',
    name: 'Resultado do Período',
    description: 'Mini-DRE com receitas, custos, despesas e lucro líquido',
    icon: Wallet,
    category: 'financial',
    defaultSize: 'half',
    requiredPermission: 'finance:view_dre',
  },
  {
    type: 'expense_composition',
    name: 'Composição de Despesas',
    description: 'Gráfico de rosca agrupando despesas por classificação DRE',
    icon: PieChart,
    category: 'financial',
    defaultSize: 'half',
    requiredPermission: 'finance:view_dre',
  },
  {
    type: 'margin_indicators',
    name: 'Margens e Performance',
    description: 'Margem de contribuição e ponto de equilíbrio',
    icon: Target,
    category: 'financial',
    defaultSize: 'third',
    requiredPermission: 'fin:view_reports',
  },

  // Vendas
  {
    type: 'sales_metrics',
    name: 'Métricas de Vendas',
    description: 'Total vendido, quantidade e ticket médio do mês',
    icon: BarChart3,
    category: 'sales',
    defaultSize: 'full',
  },
  {
    type: 'my_objectives',
    name: 'Minhas Metas',
    description: 'Acompanhamento das metas de vendas pessoais',
    icon: Target,
    category: 'sales',
    defaultSize: 'full',
  },
  {
    type: 'top_products',
    name: 'Top Produtos',
    description: 'Produtos mais vendidos no período',
    icon: TrendingUp,
    category: 'sales',
    defaultSize: 'half',
  },

  // CRM
  {
    type: 'customer_portfolio',
    name: 'Carteira de Clientes',
    description: 'Distribuição de clientes ativos e inativos',
    icon: Users,
    category: 'crm',
    defaultSize: 'half',
  },
  {
    type: 'positivity',
    name: 'Positivação',
    description: 'Taxa de clientes que compraram no período',
    icon: PieChart,
    category: 'crm',
    defaultSize: 'half',
  },
  {
    type: 'customer_registration_pending',
    name: 'Cadastros Pendentes',
    description: 'Pedidos de cadastro de clientes aguardando aprovação',
    icon: UserPlus,
    category: 'crm',
    defaultSize: 'third',
    requiredPermission: 'crm:approve_registrations',
  },

  // Tarefas
  {
    type: 'tasks_overview',
    name: 'Minhas Tarefas',
    description: 'Tarefas pendentes, atrasadas e da semana',
    icon: CheckCircle2,
    category: 'tasks',
    defaultSize: 'half',
  },

  // Logística
  {
    type: 'delivery_stats',
    name: 'Entregas do Dia',
    description: 'Status das entregas programadas para hoje',
    icon: Truck,
    category: 'logistics',
    defaultSize: 'half',
  },

  // Estoque
  {
    type: 'inventory_stats',
    name: 'Estoque e Empréstimos',
    description: 'Total de cilindros, empréstimos ativos e vencidos',
    icon: Package,
    category: 'inventory',
    defaultSize: 'full',
  },
  {
    type: 'stock_health',
    name: 'Saúde do Estoque',
    description: 'Cobertura em dias e percentual de saúde do estoque',
    icon: Activity,
    category: 'inventory',
    defaultSize: 'half',
    requiredPermission: 'stock:manual_adjust',
  },
  {
    type: 'dead_stock',
    name: 'Dinheiro Parado',
    description: 'Capital retido em produtos sem venda há 90+ dias',
    icon: TrendingDown,
    category: 'inventory',
    defaultSize: 'half',
    requiredPermission: 'stock:manual_adjust',
  },
  {
    type: 'abc_curve',
    name: 'Curva ABC de Produtos',
    description: 'Classificação de produtos por representatividade na receita',
    icon: BarChart3,
    category: 'inventory',
    defaultSize: 'half',
    requiredPermission: 'stock:manual_adjust',
  },
  {
    type: 'product_trend',
    name: 'Tendência de Produtos',
    description: 'Produtos crescendo e retraindo vs. mês anterior',
    icon: TrendingUp,
    category: 'inventory',
    defaultSize: 'half',
  },

  // Compras
  {
    type: 'purchases_pending',
    name: 'Compras Pendentes',
    description: 'Pedidos de compra aguardando aprovação',
    icon: ShoppingCart,
    category: 'purchases',
    defaultSize: 'third',
    requiredPermission: 'purchasing:approve',
  },
  {
    type: 'lost_sales_summary',
    name: 'Vendas Perdidas (Ruptura)',
    description: 'Rupturas do mês: total, top produtos e motivos',
    icon: PackageX,
    category: 'purchases',
    defaultSize: 'half',
  },

  // Atividades Recentes
  {
    type: 'recent_activity',
    name: 'Atividades Recentes',
    description: 'Últimas movimentações do sistema',
    icon: ClipboardList,
    category: 'inventory',
    defaultSize: 'full',
  },

  // Auditoria de Dados
  {
    type: 'data_audit_alert',
    name: 'Alertas de Dados',
    description: 'Problemas de integridade de dados que precisam de atenção',
    icon: AlertTriangle,
    category: 'logistics',
    defaultSize: 'half',
    requiredPermission: 'admin:security',
  },

  // Produção
  {
    type: 'production_projects',
    name: 'Projetos em Andamento',
    description: 'Projetos de produção ativos com progresso',
    icon: ClipboardList,
    category: 'production',
    defaultSize: 'half',
  },
  {
    type: 'production_orders_pending',
    name: 'Ordens Pendentes',
    description: 'Ordens de produção aguardando início',
    icon: Factory,
    category: 'production',
    defaultSize: 'third',
  },
  {
    type: 'produced_items_month',
    name: 'Itens Produzidos',
    description: 'Total de itens produzidos no mês',
    icon: Package,
    category: 'production',
    defaultSize: 'third',
  },
  {
    type: 'production_efficiency',
    name: 'Eficiência de Produção',
    description: 'Taxa de produção vs planejado',
    icon: TrendingUp,
    category: 'production',
    defaultSize: 'third',
  },
];

// Widgets padrão para Master (financeiro/gerencial)
export const DEFAULT_WIDGETS: UserWidget[] = [
  { id: 'default-1', type: 'receivables_overview', size: 'half', position: 0 },
  { id: 'default-2', type: 'payables_overview', size: 'half', position: 1 },
  { id: 'default-3', type: 'financial_accounts', size: 'half', position: 2 },
  { id: 'default-4', type: 'inventory_stats', size: 'full', position: 3 },
  { id: 'default-5', type: 'recent_activity', size: 'full', position: 4 },
];

// Widgets padrão para Operador (vendas/CRM)
export const DEFAULT_OPERATOR_WIDGETS: UserWidget[] = [
  { id: 'op-1', type: 'customer_portfolio', size: 'half', position: 0 },
  { id: 'op-2', type: 'sales_metrics', size: 'half', position: 1 },
  { id: 'op-3', type: 'my_objectives', size: 'full', position: 2 },
  { id: 'op-4', type: 'positivity', size: 'half', position: 3 },
  { id: 'op-5', type: 'tasks_overview', size: 'half', position: 4 },
];

export const getWidgetConfig = (type: string): WidgetConfig | undefined => {
  return AVAILABLE_WIDGETS.find(w => w.type === type);
};
